/**
 * Auth Context Provider
 * AD-20251113-SUPABASE-AUTH-VITE: Centralized auth state management
 *
 * Provides authentication state via React context, handling Supabase auth
 * including magic link redirects automatically via onAuthStateChange.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export interface AuthUser {
  id: string;
  email: string;
  email_confirmed_at?: string | null;
  user_metadata?: Record<string, unknown>;
}

interface AuthContextType {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Per Supabase best practices: Call getSession() once on mount, then set up onAuthStateChange listener
    // The INITIAL_SESSION event will fire immediately with the current session
    supabase.auth.getSession().then(({ data: { session }, error: sessionError }) => {
      // Debug log (non-PII)
      console.debug('[AuthContext] getSession result', {
        hasSession: !!session,
        hasError: !!sessionError,
      });
      
      if (sessionError) {
        setError(sessionError as Error);
        setUser(null);
        setSession(null);
      } else if (session?.user) {
        setSession(session);
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          email_confirmed_at: session.user.email_confirmed_at,
          user_metadata: session.user.user_metadata,
        });
      } else {
        setUser(null);
        setSession(null);
      }
    });

    // Subscribe to auth state changes
    // This automatically handles magic link redirects - when Supabase redirects
    // to our SPA with hash params, the client processes them and fires this event
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // Debug log (non-PII)
        console.debug('[AuthContext] onAuthStateChange', {
          event,
          hasSession: !!newSession,
        });
        
        // CRITICAL: Only set loading=false after INITIAL_SESSION fires
        // This prevents race conditions where components see stale auth state
        if (event === 'INITIAL_SESSION') {
          if (newSession?.user) {
            setSession(newSession);
            setUser({
              id: newSession.user.id,
              email: newSession.user.email || '',
              email_confirmed_at: newSession.user.email_confirmed_at,
              user_metadata: newSession.user.user_metadata,
            });
            setError(null);
          } else {
            setUser(null);
            setSession(null);
          }
          // Set loading to false ONLY after INITIAL_SESSION fires
          setLoading(false);
        } else if (event === 'SIGNED_IN' && newSession?.user) {
          setError(null);
          // Set both session and user together to ensure they're always in sync
          setSession(newSession);
          setUser({
            id: newSession.user.id,
            email: newSession.user.email || '',
            email_confirmed_at: newSession.user.email_confirmed_at,
            user_metadata: newSession.user.user_metadata,
          });
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
          setError(null);
        } else if (event === 'TOKEN_REFRESHED' && newSession?.user) {
          setSession(newSession);
          setUser({
            id: newSession.user.id,
            email: newSession.user.email || '',
            email_confirmed_at: newSession.user.email_confirmed_at,
            user_metadata: newSession.user.user_metadata,
          });
        } else if (event === 'USER_UPDATED' && newSession?.user) {
          setSession(newSession);
          setUser({
            id: newSession.user.id,
            email: newSession.user.email || '',
            email_confirmed_at: newSession.user.email_confirmed_at,
            user_metadata: newSession.user.user_metadata,
          });
        } else if (event === 'PASSWORD_RECOVERY') {
          // Password recovery flow - session may be null
          setSession(newSession);
          if (newSession?.user) {
            setUser({
              id: newSession.user.id,
              email: newSession.user.email || '',
              email_confirmed_at: newSession.user.email_confirmed_at,
              user_metadata: newSession.user.user_metadata,
            });
          } else {
            setUser(null);
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    user,
    session,
    loading,
    error,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * Throws error if used outside AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

